// EL_AddEditFamilyUser.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import popimg from "../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../Common/Images/ImagesForAlert/popup_close_n.png";
import CustomAlert from '../Common/CustomAlert';
import Loading from '../Common/Loading';

// Import images
import saveIcon from '../Images/save_d.gif';
import cancelIcon from '../Images/cancel_d.gif';

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

interface StudentData {
  SchoolCode: string;
  FamilyCode: string;
  Login: string;
  FName: string;
  LName: string;
  Gender: string;
  DOB: string;
  Std: string;
  Competency: string;
  UserAccountType: string;
  WLP_Competency_Mathematics: string;
  WLP_Competency_English: string;
  ExpiryDate: string;
  CanAccessLessonPlan: boolean;
  CanEarnPoints: boolean;
  Curriculum: string;
}

type CompetencyOption = [string, string];
const EL_AddEditFamilyUser: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    UserType: "0",
    FName: "",
    LName: "",
    DOB: "",
    Std: "0",
    Gender: "0",
    CompetencyEnglish: "",
    CompetencyMaths: "",
    Curriculum: "",
    CanAccessLessonPlan: false,
    CanEarnPoints: false
  });
  const [gradeOptions, setGradeOptions] = useState<Array<{ value: string, text: string }>>([]);
  const [competencyOptions, setCompetencyOptions] = useState<[string, string][]>([]);
  const [curriculumOptions, setCurriculumOptions] = useState<string[]>([]);
  const [curriculumBased, setCurriculumBased] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const schoolCode = searchParams.get("SchoolCode") || "";
  const familyCode = searchParams.get("FamilyCode") || "";
  const parentLogin = searchParams.get("Login") || "";
  const childLogin = searchParams.get("ChildLogin") || "";

  document.body.style.backgroundColor = "azure";

  // const handleSuccessClose = () => {
  //   setSuccessMessage("");
  //   navigate(
  //     `/DEALER/EL_EditFamilyProfile?` +
  //     `SchoolCode=${encodeURIComponent(schoolCode)}` +
  //     `&FamilyCode=${encodeURIComponent(familyCode)}` +
  //     `&Login=${encodeURIComponent(parentLogin)}`
  //   );
  // };


  const handleSuccessClose = () => {
    setSuccessMessage("");

    // Refresh the Edit Family Profile page that opened this popup
    if (window.opener && !window.opener.closed) {
      window.opener.location.reload();
    }

    // Close this Add/Edit Student popup
    window.close();
  };
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/get-add-edit-data?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&ParentLogin=${parentLogin}&ChildLogin=${childLogin}`
      );
      const data = await response.json();

      if (data.error) {
        setErrorMessage(data.error);
        return;
      }

      setGradeOptions(data.grade_options);
      setCompetencyOptions(data.competency_options);
      setCurriculumOptions(data.curriculum_options || []);
      setCurriculumBased(data.curriculum_based || (data.curriculum_options || []).length > 0);
      setIsEdit(data.is_edit);

      if (data.student_data) {
        const student = data.student_data;
        setFormData({
          UserType: String(student.UserAccountType || "0"),
          FName: student.FName || "",
          LName: student.LName || "",
          DOB: student.DOB
            ? student.DOB.split("/").reverse().join("-")
            : "",
          Std: String(student.Std || "0"),
          Gender: student.Gender === "M" || student.Gender === "B" ? "1" :
            (student.Gender === "F" || student.Gender === "G" ? "2" : "0"),
          CompetencyEnglish: String(student.WLP_Competency_English || student.Competency || ""),
          CompetencyMaths: String(student.WLP_Competency_Mathematics || student.Competency || ""),
          Curriculum: student.Curriculum || "",
          CanAccessLessonPlan: student.CanAccessLessonPlan || false,
          CanEarnPoints: student.CanEarnPoints || false
        });
      } else {
        // Default values for new student
        setFormData(prev => ({
          ...prev,
          CompetencyEnglish: "3",  // Default to Intermediate
          CompetencyMaths: "3"     // Default to Intermediate
        }));
      }
    } catch (error) {
      setErrorMessage("Error loading data. Please try again.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [schoolCode, familyCode, parentLogin, childLogin]);

  useEffect(() => {
    if (errorMessage) {
      alert(errorMessage);
      setErrorMessage("");
    }
  }, [errorMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target;

    if (id === "UserType") {
      setFormData(prev => {
        const newData = { ...prev, UserType: value };

        // Auto-check for Student
        if (value === "1") {
          newData.CanAccessLessonPlan = true;
          newData.CanEarnPoints = true;
        } else {
          newData.CanAccessLessonPlan = false;
          newData.CanEarnPoints = false;
        }

        // Guest name autofill
        if (value === "2") {
          newData.FName = "Guest";
        }

        return newData;
      });
    }

    else if (id === "CanAccessLessonPlan" || id === "CanEarnPoints") {
      const target = e.target as HTMLInputElement;

      setFormData(prev => ({
        ...prev,
        [id]: target.checked
      }));
    }

    else if (id === "DOB") {
      setFormData(prev => ({
        ...prev,
        DOB: value
      }));
    }
    else {
      setFormData(prev => ({
        ...prev,
        [id]: value
      }));
    }
  };

  const validateForm = () => {
    if (formData.UserType === "0") {
      alert("Please select User Type");
      return false;
    }

    if (!formData.FName.trim()) {
      alert("Please enter First Name");
      return false;
    }

    if (formData.DOB && !/^\d{4}-\d{2}-\d{2}$/.test(formData.DOB)) {
      alert("Invalid Date Format.");
      return false;
    }

    if (formData.Std === "0") {
      alert("Please select Student Grade Year");
      return false;
    }

    if (curriculumBased && !formData.Curriculum) {
      alert("Please select curriculum for student");
      return false;
    }

    return true;
  };

const handleSave = async () => {
  if (!validateForm()) return;

  setIsLoading(true);

  const saveData = {
    ...formData,
    DOB: formData.DOB
      ? formData.DOB.split("-").reverse().join("/")
      : ""
  };

  try {
    const response = await fetch(`${API_BASE}/api/save-student/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...saveData,
        UserAccountType: formData.UserType,
        WLP_Competency_English: formData.CompetencyEnglish,
        WLP_Competency_Mathematics: formData.CompetencyMaths,
        SchoolCode: schoolCode,
        FamilyCode: familyCode,
        ParentLogin: parentLogin,
        ChildLogin: childLogin,
        isEdit: isEdit
      })
    });

    const data = await response.json();

    if (data.error) {
      setErrorMessage(data.error);
      return;
    }

    // Save successful
    if (window.opener && !window.opener.closed) {
      // Refresh the Edit Family Profile page in the background
      window.opener.location.reload();
    }

    // Close Add/Edit Student popup
    window.close();

  } catch (error) {
    setErrorMessage("Error saving data. Please try again.");
    console.error(error);
  } finally {
    setIsLoading(false);
  }
};
  // const handleCancel = () => {
  //   if (window.opener) {
  //     window.opener.gfn_HidePopup();
  //   } else {
  //     window.close();
  //   }
  //   return false;
  // };
  const handleCancel = () => {
    window.close();
  };

  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return "";

    try {
      const [day, month, year] = dateStr.split('/');
      return new Date(`${year}-${month}-${day}`).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  return (

    <>
      {/* Responsive-only additions: scoped media query overrides. No existing inline styles removed or changed. */}
      <style>{`
        @media (max-width: 768px) {
          .elAddEditFamilyUser-container {
            width: 90% !important;
          }
          .elAddEditFamilyUser-select {
            width: 100% !important;
          }
          .elAddEditFamilyUser-input {
            width: 100% !important;
            box-sizing: border-box !important;
          }
        }
        @media (max-width: 480px) {
          .elAddEditFamilyUser-container {
            width: 95% !important;
            padding: 10px !important;
          }
          .elAddEditFamilyUser-form-table td {
            display: block !important;
            width: 100% !important;
          }
          .elAddEditFamilyUser-select {
            width: 100% !important;
          }
          .elAddEditFamilyUser-input {
            width: 100% !important;
            box-sizing: border-box !important;
          }
        }
      `}</style>

      <div
        style={{
          backgroundImage: `url(${popimg}), url(${popbg})`,
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundPosition: "left center, right center",
          backgroundSize: "30% 100%, 70% 100%",
          height: "16px",
          color: "white",
          padding: "7px 0px",
          fontWeight: "bold",
          fontSize: "12pt",
          fontFamily: "arial",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        {/* <span style={{ marginLeft: "10px" }}>
          Edit Student Details
        </span> */}

        <span style={{ marginLeft: "10px" }}>
          {isEdit ? "Edit Student Details" : "Add Edit Family User"}
        </span>
        {/* <img
          src={popclose}
          alt="close"
          height="30px"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/SysAdmin")} /> */}
      </div>

      {isLoading && <Loading />}

      <div
        className="elAddEditFamilyUser-container"
        style={{
          backgroundColor: "azure",
          margin: "auto",
          padding: 20,
          fontFamily: "Trebuchet MS, Arial, sans-serif",
          fontSize: "8pt",
          width: "35%",
        }}>

        {isEdit ? (
          <table
            style={{
              width: "100%",
              fontSize: "larger",
              marginBottom: "10px"
            }}
          >
            <tr>
              <td
                style={{
                  fontFamily: "Arial",
                  fontSize: "medium",
                  padding: "5px"
                }}
              >
                <b>Edit Student Details</b>
              </td>
            </tr>
          </table>
        ) : (
          <table
            style={{
              width: "100%",
              fontSize: "larger",
              marginBottom: "10px"
            }}
          >
            <tr>
              <td
                style={{
                  fontFamily: "Arial",
                  fontSize: "medium",
                  padding: "5px"
                }}
              >
                <b>Add Student Details</b>
              </td>
            </tr>
          </table>
        )}

        {/* Form */}
        <table className="elAddEditFamilyUser-form-table" cellPadding="9" style={{
          width: "100%",
          border: "0",
          fontFamily: "Arial",
          fontSize: "medium"
        }}>
          {/* User Type */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              User Type <span style={{ color: "red" }}>*</span>
            </td>
            <td>
              <select
                className="elAddEditFamilyUser-select"
                id="UserType"
                value={formData.UserType}
                onChange={handleInputChange}
                style={{
                  width: "160px",
                  border: "solid 1px #003385",
                  backgroundColor: "white",
                  fontFamily: "Calibri",
                  fontSize: "12pt",
                  color: "#003366"
                }}
              >
                <option value="0">--Select--</option>
                <option value="1">Student</option>
                <option value="2">Guest</option>
                <option value="3">Parent</option>
              </select>
            </td>
          </tr>

          {/* First Name */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              First Name <span style={{ color: "red" }}>*</span>
            </td>
            <td>
              <input
                className="elAddEditFamilyUser-input"
                type="text"
                id="FName"
                value={formData.FName}
                autoComplete="off"
                onChange={handleInputChange}
                style={{
                  width: "156px",
                  height: "20px",
                  fontFamily: "Trebuchet MS",
                  fontSize: "10pt",
                  border: "solid 1px gray",
                  backgroundColor: "white"
                }}
                maxLength={30} />
            </td>
          </tr>

          {/* Last Name */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              Last Name
            </td>
            <td>
              <input
                className="elAddEditFamilyUser-input"
                type="text"
                id="LName"
                value={formData.LName}
                autoComplete="off"
                onChange={handleInputChange}
                style={{
                  width: "156px",
                  height: "20px",
                  fontFamily: "Trebuchet MS",
                  fontSize: "10pt",
                  border: "solid 1px gray",
                  backgroundColor: "white"
                }}
                maxLength={30} />
            </td>
          </tr>

          {/* Date of Birth */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              Date of Birth
            </td>
            <td>
              <input
                className="elAddEditFamilyUser-input"
                type="date"
                id="DOB"
                value={formData.DOB}
                onChange={handleInputChange}
                style={{
                  width: "156px",
                  height: "20px",
                  fontFamily: "Trebuchet MS",
                  fontSize: "10pt",
                  border: "solid 1px gray",
                  backgroundColor: "white"
                }}
              />
            </td>
          </tr>

          {/* Grade/Year */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              Grade/Year <span style={{ color: "red" }}>*</span>
            </td>
            <td>
              <select
                className="elAddEditFamilyUser-select"
                id="Std"
                value={formData.Std}
                onChange={handleInputChange}
                style={{
                  width: "160px",
                  border: "solid 1px #003385",
                  backgroundColor: "white",
                  fontFamily: "Calibri",
                  fontSize: "12pt",
                  color: "#003366"
                }}
              >
                <option value="0">--Select--</option>
                {gradeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </td>
          </tr>

          {/* Gender */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              Gender
            </td>
            <td>
              <select
                className="elAddEditFamilyUser-select"
                id="Gender"
                value={formData.Gender}
                onChange={handleInputChange}
                style={{
                  width: "160px",
                  border: "solid 1px #003385",
                  backgroundColor: "white",
                  fontFamily: "Calibri",
                  fontSize: "12pt",
                  color: "#003366"
                }}
              >
                <option value="0">--Select--</option>
                <option value="1">Male</option>
                <option value="2">Female</option>
              </select>
            </td>
          </tr>

          {/* Competency English */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              Competency_English
            </td>
            <td>
              <select
                className="elAddEditFamilyUser-select"
                id="CompetencyEnglish"
                value={formData.CompetencyEnglish}
                onChange={handleInputChange}
                style={{
                  width: "160px",
                  border: "solid 1px #003385",
                  backgroundColor: "white",
                  fontFamily: "Calibri",
                  fontSize: "12pt",
                  color: "#003366"
                }}
                disabled={isEdit}
              >
                <option value="">--Select--</option>
                {competencyOptions.map(([text, value]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </td>
          </tr>

          {/* Competency Maths */}
          <tr>
            <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
              Competency_Maths
            </td>
            <td>
              <select
                className="elAddEditFamilyUser-select"
                id="CompetencyMaths"
                value={formData.CompetencyMaths}
                onChange={handleInputChange}
                style={{
                  width: "160px",
                  border: "solid 1px #003385",
                  backgroundColor: "white",
                  fontFamily: "Calibri",
                  fontSize: "12pt",
                  color: "#003366"
                }}
                disabled={isEdit}
              >
                <option value="">--Select--</option>
                {competencyOptions.map(([text, value]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </td>
          </tr>

          {/* Curriculum (if enabled) */}
          {curriculumBased && (
            <tr style={{ display: curriculumBased ? "table-row" : "none" }}>
              <td style={{ fontFamily: "Arial", fontSize: "medium" }}>
                Curriculum <span style={{ color: "red" }}>*</span>
              </td>
              <td>
                <select
                  className="elAddEditFamilyUser-select"
                  id="Curriculum"
                  value={formData.Curriculum}
                  onChange={handleInputChange}
                  style={{
                    width: "160px",
                    border: "solid 1px #003385",
                    backgroundColor: "white",
                    fontFamily: "Calibri",
                    fontSize: "12pt",
                    color: "#003366"
                  }}
                >
                  <option value="">--Select--</option>
                  {curriculumOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          )}

          {/* Can Access Lesson Plan */}
          <tr>
            <td colSpan={2} style={{ fontFamily: "Arial", fontSize: "medium" }}>
              <input
                type="checkbox"
                id="CanAccessLessonPlan"
                checked={formData.CanAccessLessonPlan}
                onChange={handleInputChange}
                disabled={formData.UserType === "2" || formData.UserType === "3"} />
              <label htmlFor="CanAccessLessonPlan" style={{ fontFamily: "Arial", fontSize: "medium" }}>
                Can Access Lesson Plan
              </label>
            </td>
          </tr>

          {/* Can Earn Points */}
          <tr>
            <td colSpan={2} style={{ fontFamily: "Arial", fontSize: "medium" }}>
              <input
                type="checkbox"
                id="CanEarnPoints"
                checked={formData.CanEarnPoints}
                onChange={handleInputChange}
                disabled={formData.UserType === "2" || formData.UserType === "3"} />
              <label htmlFor="CanEarnPoints" style={{ fontFamily: "Arial", fontSize: "medium" }}>
                Can Earn Points
              </label>
            </td>
          </tr>

          {/* Buttons */}
          <tr style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "185%" }}>
            <td>
              <img
                src={saveIcon}
                alt="Save"
                onClick={handleSave}
                onMouseOver={(e) => e.currentTarget.src = saveIcon}
                onMouseOut={(e) => e.currentTarget.src = saveIcon}
                onMouseDown={(e) => e.currentTarget.src = saveIcon}
                style={{ cursor: "pointer" }} />
            </td>
            <td>
              <img
                src={cancelIcon}
                alt="Cancel"
                onClick={handleCancel}
                onMouseOver={(e) => e.currentTarget.src = cancelIcon}
                onMouseOut={(e) => e.currentTarget.src = cancelIcon}
                onMouseDown={(e) => e.currentTarget.src = cancelIcon}
                style={{ cursor: "pointer" }} />
            </td>
          </tr>
        </table>
      </div>

      {/* {successMessage && (
        <CustomAlert
          message={successMessage}
          onClose={handleSuccessClose}
        />
      )} */}
    </>
  );
};

export default EL_AddEditFamilyUser;